/**
 * User-facing labels for `TripleWarning` and the rest of the
 * `@ardoq/metamodel-constraints` surface.
 *
 * Pattern A: this package stays translation-free. Consumers may pass a
 * partial overrides object via `labels`; missing keys fall back to the
 * English defaults declared here. Templated variants are functions so
 * consumers can produce locale-correct word ordering rather than relying
 * on naïve string concatenation.
 */

export type TripleWarningLabels = {
  /**
   * Inline link text rendered inside the "isn't approved yet" warning. Admins
   * are linked to the constraints management screen; non-admins to the
   * help-center article on metamodel constraints.
   */
  metamodelConstraintsInlineLink: string;
  /**
   * Info-banner nudge shown when the user picks the Implicit reference type
   * even though approved named types exist for the pair, when the count of
   * those approved types is known.
   */
  implicitReferenceTypeWarningWithCount: (args: {
    count: number;
    sourceType: string;
    targetType: string;
  }) => string;
  /**
   * Info-banner nudge shown when the user picks the Implicit type but the pair
   * has no approved named types (so there is no count to show).
   */
  implicitReferenceTypeWarning: string;
  /**
   * The "isn't approved yet" warning sentence (including the "may not appear"
   * consequence), shown for any unapproved reference type. Followed by the
   * admin/non-admin lead-in and the inline metamodel-constraints link.
   */
  referenceTypeViolatesConstraintsWarning: (args: {
    referenceType: string;
    sourceType: string;
    targetType: string;
  }) => string;
  /**
   * Lead-in after the warning sentence when the user can manage constraints,
   * followed by the inline metamodel-constraints link.
   */
  referenceNotApprovedAdminDetail: string;
  /**
   * Lead-in after the warning sentence when the user cannot manage constraints.
   */
  referenceNotApprovedNonAdminDetail: string;
};

export const DEFAULT_TRIPLE_WARNING_LABELS: TripleWarningLabels = {
  metamodelConstraintsInlineLink: 'metamodel constraints',
  implicitReferenceTypeWarningWithCount: ({ count, sourceType, targetType }) =>
    count === 1
      ? `There is 1 approved reference type from "${sourceType}" to "${targetType}". We recommend using 'Implicit' only when it doesn't match your needs.`
      : `There are ${count} approved reference types from "${sourceType}" to "${targetType}". We recommend using 'Implicit' only when none of them match your needs.`,
  implicitReferenceTypeWarning:
    "We recommend using 'Implicit' only when none of the existing reference types match your needs.",
  referenceTypeViolatesConstraintsWarning: ({
    referenceType,
    sourceType,
    targetType,
  }) =>
    `"${referenceType}" reference type from "${sourceType}" to "${targetType}" isn't approved yet, so this reference may not appear in some views and reports.`,
  referenceNotApprovedAdminDetail:
    'If this seems like a valid path, add it to your',
  referenceNotApprovedNonAdminDetail:
    'If this seems like a valid path, contact your admin to get it approved in the',
};

export const mergeTripleWarningLabels = (
  overrides?: Partial<TripleWarningLabels>
): TripleWarningLabels => ({
  ...DEFAULT_TRIPLE_WARNING_LABELS,
  ...overrides,
});
